"use client";

import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import { createSafeStorage } from './safe-storage';

// Reuse types from components where helpful to avoid duplication
// Client type is imported from the Agency client detail component to keep shapes aligned
import type { ClientDetailData } from '@/components/agency/ClientDetail';

// Shared helpers
export const getAuthHeaders = (): HeadersInit => {
  if (typeof window === 'undefined') return {};
  
  // Try to get token from localStorage (used by auth-store)
  const token = localStorage.getItem('token');
  
  // Also try auth-storage (Zustand persist) as fallback
  if (!token) {
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        const storedToken = parsed?.state?.token;
        if (storedToken) {
          return { Authorization: `Bearer ${storedToken}` };
        }
      }
    } catch {}
  }
  
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Agency slice
export interface AgencyInfo {
  agency: {
    id: number;
    name: string;
    plan: string;
    seat_limit: number;
    client_limit: number;
    subdomain?: string | null;
    custom_domain?: string | null;
    agency_logo_url?: string | null;
    website?: string | null;
    owner_user_id?: number;
    owner_name?: string | null;
    owner_email?: string | null;
    created_at?: string;
    updated_at?: string;
  };
  limits: { seatLimit: number; clientLimit: number };
}

export interface AgencySliceState {
  agency: AgencyInfo | null;
  clients: ClientDetailData[];
  selectedClientId: number | null;
  isAgencyLoading: boolean;
  isClientsLoading: boolean;
  agencyError?: string;
  clientsError?: string;
  needsAgencySetup?: boolean;
  clientEngagement: Record<number, number>;
}

export interface AgencySliceActions {
  loadAgency: () => Promise<void>;
  loadClients: () => Promise<void>;
  refreshAgencyAndClients: () => Promise<void>;
  setSelectedClientId: (id: number | null) => void;
  updateClientInStore: (client: ClientDetailData) => void;
  deleteClientById: (id: number) => Promise<boolean>;
  updateAgencyInStore: (updates: Partial<AgencyInfo['agency']>) => void;
}

export type AgencySlice = AgencySliceState & AgencySliceActions;

const createAgencySlice = (set: any, get: any): AgencySlice => ({
  agency: null,
  clients: [],
  selectedClientId: null,
  isAgencyLoading: false,
  isClientsLoading: false,
  agencyError: undefined,
  clientsError: undefined,
  needsAgencySetup: false,
  clientEngagement: {},

  loadAgency: async () => {
    try {
      set({ isAgencyLoading: true, agencyError: undefined });
      const res = await fetch('/api/agency/me', {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        cache: 'no-store',
      });
      if (!res.ok) {
        const text = await res.text();
        const is404 = res.status === 404;
        set({ agencyError: text || 'Failed to load agency', isAgencyLoading: false, needsAgencySetup: is404 });
        return;
      }
      const data: AgencyInfo = await res.json();
      set({ agency: data, isAgencyLoading: false, agencyError: undefined, needsAgencySetup: false });
    } catch (e: any) {
      set({ agencyError: e?.message || 'Failed to load agency', isAgencyLoading: false });
    }
  },

  loadClients: async () => {
    try {
      set({ isClientsLoading: true, clientsError: undefined });
      const res = await fetch('/api/agency/clients', {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        cache: 'no-store',
      });
      if (!res.ok) {
        const text = await res.text();
        set({ clientsError: text || 'Failed to load clients', isClientsLoading: false });
        return;
      }
      const data = await res.json();
      const clients: ClientDetailData[] = data?.clients || [];
      set((state: AgencySlice) => {
        const validIds = new Set(clients.map((client) => client.id));
        const nextEngagement: Record<number, number> = {};

        for (const [key, value] of Object.entries(state.clientEngagement || {})) {
          const clientId = Number(key);
          if (validIds.has(clientId) && typeof value === 'number') {
            nextEngagement[clientId] = value;
          }
        }

        return {
          clients,
          isClientsLoading: false,
          clientsError: undefined,
          clientEngagement: nextEngagement,
        };
      });
    } catch (e: any) {
      set({ clientsError: e?.message || 'Failed to load clients', isClientsLoading: false });
    }
  },

  refreshAgencyAndClients: async () => {
    await Promise.all([get().loadAgency(), get().loadClients()]);
  },

  setSelectedClientId: (id) =>
    set((state: AgencySlice) => {
      if (typeof id !== 'number') {
        return { selectedClientId: id };
      }

      return {
        selectedClientId: id,
        clientEngagement: {
          ...state.clientEngagement,
          [id]: Date.now(),
        },
      };
    }),

  updateClientInStore: (client) => {
    const next = (get().clients as ClientDetailData[]).map((c) => (c.id === client.id ? { ...c, ...client } : c));
    set({ clients: next });
  },

  deleteClientById: async (id: number) => {
    try {
      const res = await fetch(`/api/agency/clients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) return false;
      const filtered = (get().clients as ClientDetailData[]).filter((c) => c.id !== id);
      set((state: AgencySlice) => {
        const nextEngagement = { ...state.clientEngagement };
        delete nextEngagement[id];
        const nextSelectedClientId = state.selectedClientId === id ? null : state.selectedClientId;

        return {
          clients: filtered,
          selectedClientId: nextSelectedClientId,
          clientEngagement: nextEngagement,
        };
      });
      return true;
    } catch {
      return false;
    }
  },

  updateAgencyInStore: (updates) => {
    const current = get().agency as AgencyInfo | null;
    if (!current) return;
    const next: AgencyInfo = {
      ...current,
      agency: {
        ...current.agency,
        ...updates,
      },
    };
    set({ agency: next });
  },
});

// Root app store
export type AppState = AgencySlice;

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        // Slices
        ...createAgencySlice(set, get),
      }),
      {
        name: 'app-store',
        storage: createJSONStorage(() => createSafeStorage()),
        partialize: (state: AppState) => ({
          // Persist minimal, non-sensitive UI state
          agency: state.agency,
          clients: state.clients,
          selectedClientId: state.selectedClientId,
          clientEngagement: state.clientEngagement,
        }),
      }
    ),
    { name: 'App Store' }
  )
);


